import type { AssetTypeDto, AssetTypeFieldDto } from "../data";

const ACCOUNTS: readonly AssetTypeDto["account"][] = [
  { id: "company-01", nameEn: "Acme Corp", nameAr: "شركة أكمي" },
  { id: "company-02", nameEn: "Globex Inc", nameAr: "شركة جلوبكس" },
  { id: "company-03", nameEn: "Initech", nameAr: "شركة إينيتك" },
  { id: "company-04", nameEn: "Umbrella Co", nameAr: "شركة أمبريلا" },
  { id: "company-05", nameEn: "Soylent Corp", nameAr: "شركة سوילنت" },
  { id: "company-06", nameEn: "Stark Industries", nameAr: "صناعات ستارك" },
  { id: "company-07", nameEn: "Wayne Enterprises", nameAr: "مؤسسات واين" },
];

const FIELD_TYPES: readonly AssetTypeFieldDto["type"][] = [
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "DATE",
];

const date = (index: number) =>
  `2026-03-${String((index % 28) + 1).padStart(2, "0")}T09:00:00.000Z`;

function fieldsFor(assetTypeId: string, index: number): AssetTypeFieldDto[] {
  // Every type starts with the two default name fields (see
  // AssetTypeFormScreen), plus a varying number of custom ones.
  const base: AssetTypeFieldDto[] = [
    {
      id: `${assetTypeId}-field-01`,
      nameEn: "Asset Name (Arabic)",
      nameAr: "اسم الأصل بالعربية",
      type: "STRING",
      isRequired: true,
      isUnique: true,
      sortOrder: 0,
      createdAt: date(index),
      updatedAt: date(index),
      assetTypeId,
      createdById: "user-01",
    },
    {
      id: `${assetTypeId}-field-02`,
      nameEn: "Asset Name (English)",
      nameAr: "اسم الأصل بالإنجليزية",
      type: "STRING",
      isRequired: true,
      isUnique: true,
      sortOrder: 1,
      createdAt: date(index),
      updatedAt: date(index),
      assetTypeId,
      createdById: "user-01",
    },
  ];
  const customCount = index % 4; // 0-3 extra fields
  const custom: AssetTypeFieldDto[] = Array.from(
    { length: customCount },
    (_, offset) => ({
      id: `${assetTypeId}-field-${offset + 3}`,
      nameEn: `Custom Field ${offset + 1}`,
      nameAr: `حقل ${offset + 1}`,
      type: FIELD_TYPES[(index + offset) % FIELD_TYPES.length]!,
      isRequired: offset % 2 === 0,
      isUnique: false,
      sortOrder: offset + 2,
      createdAt: date(index),
      updatedAt: date(index),
      assetTypeId,
      createdById: "user-01",
    }),
  );
  return [...base, ...custom];
}

// Every generated row is free to have its custom fields deleted from
// (relatedAssetsCount: 0) except this one, dedicated to exercising the
// "can't remove a field while assets exist" rule.
export const assetTypeWithAttachedAssetsId = "asset-type-07";

export const assetTypeFixtures: readonly AssetTypeDto[] = Array.from(
  { length: 28 },
  (_, offset) => {
    const index = offset + 1;
    const id = `asset-type-${String(index).padStart(2, "0")}`;
    const account = ACCOUNTS[offset % ACCOUNTS.length]!;
    return {
      id,
      nameEn: `Asset Type ${String(index).padStart(2, "0")}`,
      nameAr: `نوع ${index}`,
      remarks: index % 5 === 0 ? null : "Fixture remarks.",
      attachmentsAllowed: index % 2 === 0,
      status: index % 3 === 0 ? "INACTIVE" : "ACTIVE",
      createdAt: date(index),
      updatedAt: date(index + 1),
      accountId: account.id,
      account,
      createdById: "user-01",
      updatedById: null,
      fields: fieldsFor(id, index),
      relatedAssetsCount: id === assetTypeWithAttachedAssetsId ? 2 : 0,
    };
  },
);

// Seeds the create-time archived-match flow -- a create attempt whose name
// matches this row offers a restore instead of creating a duplicate. Shape
// mirrors organizationFixtures' archived rows: a full record plus archivedAt
// and fieldsCount (the trimmed GET /asset-types/archived row's own field
// count -- deliberately not derived from `fields.length` below, same as the
// real endpoint's own independent count).
export const archivedAssetTypeFixtures: readonly (AssetTypeDto & {
  archivedAt: string;
  fieldsCount: number;
})[] = [
  {
    id: "asset-type-archived-01",
    nameEn: "Archived Asset Type",
    nameAr: "نوع مؤرشف",
    remarks: null,
    attachmentsAllowed: false,
    status: "ACTIVE",
    createdAt: date(2),
    updatedAt: date(3),
    accountId: "company-05",
    account: ACCOUNTS[4]!,
    createdById: "user-01",
    updatedById: null,
    fields: fieldsFor("asset-type-archived-01", 2),
    // Deliberately later than every padding row below (dated in January) so
    // this row always sorts onto page 1 regardless of the default sort
    // direction -- the archived-list screen test relies on finding it there.
    archivedAt: "2026-02-15T09:00:00.000Z",
    fieldsCount: 12,
  },
  // A second, distinct row so the "restore" test (which consumes it) doesn't
  // starve the "archived-match" test above of a row to match against --
  // same one-row-per-test convention as the rest of this file.
  {
    id: "asset-type-archived-02",
    nameEn: "Archived Asset Type Two",
    nameAr: "نوع مؤرشف اثنان",
    remarks: null,
    attachmentsAllowed: false,
    status: "ACTIVE",
    createdAt: date(4),
    updatedAt: date(5),
    accountId: "company-06",
    account: ACCOUNTS[5]!,
    createdById: "user-01",
    updatedById: null,
    fields: fieldsFor("asset-type-archived-02", 4),
    archivedAt: "2026-02-16T09:00:00.000Z",
    fieldsCount: 5,
  },
  // Padding rows purely so the archived list screen has enough data to
  // demonstrate real pagination -- not referenced by id in any test.
  ...Array.from({ length: 23 }, (_, offset) => {
    const index = offset + 3;
    const id = `asset-type-archived-${String(index).padStart(2, "0")}`;
    const account = ACCOUNTS[offset % ACCOUNTS.length]!;
    const status: "ACTIVE" | "INACTIVE" =
      index % 4 === 0 ? "INACTIVE" : "ACTIVE";
    return {
      id,
      nameEn: `Archived Asset Type ${String(index).padStart(2, "0")}`,
      nameAr: `نوع مؤرشف ${index}`,
      remarks: null,
      attachmentsAllowed: index % 2 === 0,
      status,
      createdAt: date(index),
      updatedAt: date(index + 1),
      accountId: account.id,
      account,
      createdById: "user-01",
      updatedById: null,
      fields: fieldsFor(id, index),
      archivedAt: `2026-01-${String((index % 28) + 1).padStart(2, "0")}T09:00:00.000Z`,
      fieldsCount: index % 9,
    };
  }),
];
